import { useState } from "react";
import { Button, Card, DatePicker, Form, Input, message, Select } from "antd";
import { ChevronRight, User } from "lucide-react";
import { FormattedMessage } from "react-intl";

import { payTypes } from "sr/i18n/messages/setting";
import { employment } from "../../jobpost/component/Jsonalldata";
import { createContractor } from "sr/utils/api/company/workorder/createContractor";
import { updateApplication } from "sr/utils/api/cleaner/updateApplication";
import { contractorHire } from "sr/utils/api/company/contractor/contractorHire";

function Step1(props: any) {
  const [isLoading2, setIsLoading2] = useState(false);
  const app = props?.application;

  const submit = async (values: any) => {
    setIsLoading2(true);

    try {
      if (props?.data?.id) {
        props?.onChange(props?.data);
        return;
      }

      const payload1 = {
        cleaner_id: app?.cleaner_id?.id,
        company_id: props?.company_id,
        status: "active",
      };

      const response1 = await createContractor(payload1);

      if (!response1?.success) {
        message.error(response1?.msg || "Failed to create contractor.");
        return;
      }

      const payload2 = {
        id: app?.id,
        status: "hired",
      };

      const response2 = await updateApplication(payload2);

      if (!response2?.success) {
        if (response2?.msg === "APPLICATION.HIRE_COUNT.EXCEEDED") {
          message.error(
            "The maximum hire count for this application has been exceeded.",
          );
        } else {
          message.error(response2?.msg || "Failed to update application.");
        }

        return;
      }

      const payload3 = {
        companyId: props?.company_id,
        contractor_id: response1?.data?.id,
        joining_date: values?.joining_date,
        employment_type: values?.employment_type,
        destination: app?.job_id?.job_title,
        rate: values?.rate,
      };

      const response3 = await contractorHire(payload3);

      if (!response3?.success) {
        message.error(response3?.msg || "Failed to hire contractor.");
        return;
      }

      message.success("Candidate Updated to Contractor");

      props?.onChange({
        ...response1?.data,
        ...payload3,
      });
    } catch (err) {
      console.log("error = = = >", err);
      message.error("Something went wrong");
    } finally {
      // This ALWAYS runs, including after return
      setIsLoading2(false);
    }
  };

  const updateJob = async () => {};

  return (
    <div className="mt-6 mx-auto max-w-4xl pb-40">
      <Form
        layout="vertical"
        onFinish={submit}
        initialValues={{
          joining_date: props?.data?.joining_date,
          employment_type: props?.data?.employment_type,
          rate: props?.data?.rate,
        }}
      >
        <Card loading={props?.isLoading} size="small">
          <div className="flex gap-4">
            <div className="bg-slate-100 h-14 w-14 rounded-full flex items-center justify-center">
              <User className="text-slate-500" />
            </div>
            <div>
              <div className="font-bold text-lg">
                {app?.cleaner_id?.first_name} {app?.cleaner_id?.last_name}
              </div>
              <div>
                <a
                  href={`mailto:${app?.cleaner_id?.email}`}
                  className="text-blue-400"
                >
                  {app?.cleaner_id?.email}
                </a>{" "}
                - {app?.cleaner_id?.address?.address_line_1}
              </div>
              <div>
                {app?.job_id?.job_title} -{" "}
                {app?.job_id?.job_advanced_id?.reporting_address
                  ?.address_line_1 || ""}{" "}
                {app?.job_id?.job_advanced_id?.reporting_address
                  ?.address_line_2 || ""}
              </div>
            </div>
          </div>
        </Card>
        <br />
        <Card title="Contractor Information" headStyle={{ padding: 10 }}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item
              label={
                <FormattedMessage
                  id="RECRUITER.CANDIDATES.HIRE.JOINING.DATE"
                  defaultMessage="Joining Date"
                />
              }
              name={"joining_date"}
              rules={[{ required: true, message: "Joining date is required" }]}
            >
              <DatePicker size="large" style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item
              label={
                <FormattedMessage id="CLEANER.JOBS.FILTER.EMPLOYMENTTYPE" />
              }
              name={"employment_type"}
              rules={[
                { required: true, message: "Employment Type is required" },
              ]}
            >
              <Select placeholder="Select Employment Type" size="large">
                {employment.map((item, index) => (
                  <Select.Option key={index} value={item?.value}>
                    <FormattedMessage id={item?.label} />
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item label={"Show Pay By"}>
              <Input
                placeholder="Pay By"
                size="large"
                disabled
                value={app?.job_id?.show_pay_by}
              />
            </Form.Item>

            <Form.Item label={"Rate"}>
              <Select
                placeholder="Select Employment Type"
                size="large"
                value={app?.job_id?.rate}
                disabled
              >
                {payTypes.map((item, index) => (
                  <Select.Option key={index} value={item?.value}>
                    <FormattedMessage id={item?.label} />
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {app?.job_id?.show_pay_by === "Range" ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Form.Item label={"Minimum Rate"}>
                  <Input
                    placeholder="Pay By"
                    size="large"
                    prefix={"$"}
                    disabled
                    value={app?.job_id?.min_amount}
                  />
                </Form.Item>
                <Form.Item label={"Maximum Rate"}>
                  <Input
                    placeholder="Pay By"
                    size="large"
                    prefix={"$"}
                    disabled
                    value={app?.job_id?.max_amount}
                  />
                </Form.Item>
              </div>
            ) : (
              <Form.Item label={"Minimum Rate"}>
                <Input
                  placeholder="Pay By"
                  size="large"
                  prefix={"$"}
                  disabled
                  value={
                    app?.job_id?.show_pay_by === "Starting amount"
                      ? app?.job_id?.min_amount
                      : app?.job_id?.show_pay_by === "Maximum amount"
                      ? app?.job_id?.max_amount
                      : app?.job_id?.show_pay_by === "Exact amount"
                      ? app?.job_id?.exact_amount
                      : 0
                  }
                />
              </Form.Item>
            )}
            <Form.Item
              label={"Pay Rate (Hourly)"}
              name={"rate"}
              rules={[{ required: true, message: "Pay Rate is required" }]}
            >
              <Input placeholder="Enter Rate" size="large" prefix={"$"} />
            </Form.Item>
          </div>
        </Card>
        <div className="fixed bottom-0 left-0 right-0 bg-white backdrop-blur-sm border-t border-gray-200 z-1000 p-2 !pl-0 sm:!pl-[200px]">
          <div className="flex items-center mx-auto sm:max-w-4xl">
            <div>
              <Button
                size="large"
                danger
                className="!px-10"
                onClick={() => props?.onBack()}
              >
                Close
              </Button>
            </div>
            <div className="flex-1"></div>
            <div>
              <Button
                size="large"
                type="primary"
                className="!px-10"
                htmlType="submit"
                loading={isLoading2}
              >
                Next <ChevronRight size={20} />
              </Button>
            </div>
          </div>
        </div>
      </Form>
    </div>
  );
}

export default Step1;
